import { State, TeamMembersState } from '../redux/configure-store';
import {
  InviteRowData, TeamMembersTableData, TeamTableRowType, UserRowData,
  TeamMemberTableRowData, MembersTableState, SortDirection, isUserRowData,
  MembersColumnTypes, TeamRowData, isInviteRowData, isTeamRowData
} from '../common/types';
import { InviteData, UserData, TeamData } from '../types/teamMembers';
import { InviteStatusType } from './server-types';
import { isOwner } from '../redux/selectors/roleAreas';

export const convertTeamMembersDataToTableData = (teamMembers: TeamMembersState): TeamMembersTableData => {
  const data: TeamMembersTableData = [];
  teamMembers.users.forEach(user => data.push(getRowFromUser(user)));
  teamMembers.invites.forEach(user => data.push(getRowFromInvite(user)));
  teamMembers.teams?.forEach(team => data.push(getRowFromTeam(team)));
  return data;
};

export const getRowFromUser = ({ name, email, roles, joinedTeamAt, profileImage, id: userId, isPendingOwner }: UserData): UserRowData => ({
  type: TeamTableRowType.user, name, email, roles, joinedTeamAt, profileImage, userId, isPendingOwner
});

export const getRowFromInvite = ({ email, roles, id: inviteId, acceptLink, status, dateCreated, isPendingOwner }: InviteData): InviteRowData => ({
  type: TeamTableRowType.invite, email, roles, inviteId, acceptLink, status, dateCreated, isPendingOwner
});

export const getRowFromTeam = ({ accountImage, accountName, id, roles }: TeamData): TeamRowData => ({
  type: TeamTableRowType.team, accountId: id, accountImage, accountName, roles
});

export const queryMembersTable = (members: TeamMembersTableData, membersTableState: MembersTableState, roleIds: string[]): TeamMembersTableData => {
  const namesAndEmails = membersTableState.filters?.namesAndEmails?.toLocaleLowerCase();
  if (membersTableState.filters.status) {
    if (Object.values(InviteStatusType).includes(membersTableState.filters.status)) {
      members = members.filter(member => member.type === TeamTableRowType.invite && member.status === membersTableState.filters.status);
    } else {
      members = members.filter(member => member.isPendingOwner);
    }
  }
  if (namesAndEmails) {
    members = members.filter(member => {
      if (isUserRowData(member)) {
        return member.name.firstName.toLocaleLowerCase().includes(namesAndEmails) ||
          member.name.lastName.toLocaleLowerCase().includes(namesAndEmails) ||
          member.email.toLocaleLowerCase().includes(namesAndEmails);
      } else if (isInviteRowData(member)) {
        return member.email.toLocaleLowerCase().includes(namesAndEmails);
      } else {
        return member.accountName.toLocaleLowerCase().includes(namesAndEmails);
      }
    });
  }

  return membersTableState.filters.roles.length ?
    members.filter((member: TeamMemberTableRowData) =>
      !member.roles?.length || member.roles.find(role => roleIds.includes(role) && !membersTableState.filters.roles.includes(role)))
    : members;
};

export const sortingTableMembers = (membersTableState: MembersTableState, currentUserId: string, state: State) => (a: TeamMemberTableRowData, b: TeamMemberTableRowData) => {
  if (!membersTableState.sort.column) {
    const aScore = defaultSortPriority(a, currentUserId, state);
    const bScore = defaultSortPriority(b, currentUserId, state);
    return bScore - aScore;
   }

  // Sorting by role will consider only the first role of the team member
  const selectorByRole = selectorsByRole[membersTableState.sort.column];
  const firstComparedValue = selectorByRole(a);
  const secondComparedValue = selectorByRole(b);
  if (!firstComparedValue) {
    return !secondComparedValue ? 0 : -1;
  } else if (!secondComparedValue) {
    return 1;
  }
  const comparedValue = firstComparedValue.localeCompare(secondComparedValue);
  return membersTableState.sort.direction === SortDirection.asc ? comparedValue : comparedValue * -1;
};

const defaultSortPriority = (x: TeamMemberTableRowData, currentUserId: string, state: State) => {
  // Default sort priority: owner > my self > pending > invites > users
  let score = 0;
  if (x.roles.some(role => isOwner(state, role))) { score += 1000; }
  if (x.type === TeamTableRowType.user && x.userId === currentUserId) { score += 100; }
  if (x.type === TeamTableRowType.invite) {
    score += 10;
    if (x.status === InviteStatusType.PENDING) { score += 1; }
  }
  return score;
};

export const selectorsByRole = {
  [MembersColumnTypes.NAME]: (member: TeamMemberTableRowData) => isInviteRowData(member) ? member.email : (isUserRowData(member) ? member.name.firstName || member.email : member.accountName),
  [MembersColumnTypes.ROLE]: (member: TeamMemberTableRowData) => member.roles[0],
  [MembersColumnTypes.DATE]: (member: TeamMemberTableRowData) => isTeamRowData(member) ? '' : (isUserRowData(member) ? member.joinedTeamAt : member.dateCreated)?.getTime().toString()
};
